/**
 * Toast store — leaf module. Transient toast notification queue.
 *
 * Moved out of store/index.js (Perf 3.1.b). The only cross-store dependency is
 * a RUNTIME auth lookup (to silently drop anon "sign in" toasts) — resolved
 * lazily so this leaf does not statically import the auth slice.
 */
import { create } from 'zustand';

// Lazy auth accessor — runtime-only (see store/modal.js for rationale).
// Points at the auth leaf directly (Perf 3.1.b Step 3).
const getAuthStore = () => require('./auth').useAuthStore;

export const useToastStore = create((set, get) => ({
  // State
  toasts: [],
  // Actions
  actions: {
    addToast: (message, type = 'default', actions = [], options = {}) => {
      // Public-dashboard Phase 8: anon users who slip past the Phase 6 / 7
      // gates and trigger a private endpoint get a `NO_AUTH_TOKEN` 401 from
      // `fetchWithAuth`, which surfaces as the canned "Sign in to continue."
      // string. The auth CTA flow is the source of truth for asking them to
      // sign in, so we silently drop those toasts here instead of patching
      // every caller's try/catch block.
      const isAnonAuthMessage = typeof message === 'string'
        && (message === 'Sign in to continue.' || message === 'Session expired. Please log in again.');
      if (isAnonAuthMessage) {
        const auth = getAuthStore().getState();
        const isAnon = !auth?.token || (typeof auth?.isTokenExpired === 'function' && auth.isTokenExpired());
        if (isAnon) return;
      }

      const id = Date.now();
      const newToast = {
        id,
        message,
        type,
        actions,
        category: options?.category || null,
      };

      set(state => ({
        toasts: [...state.toasts, newToast]
      }));

      const duration = type === 'earn' ? 7000 : 5000;
      setTimeout(() => get().actions.removeToast(id), duration);
      return id;
    },
    removeToast: (id) => {
      set(state => ({
        toasts: state.toasts.filter(t => t.id !== id)
      }));
    },
    clearToasts: () => {
      set({ toasts: [] });
    },
    removeToastsByCategory: (category) => {
      if (!category) return;
      set(state => ({
        toasts: state.toasts.filter(t => t.category !== category),
      }));
    },
  },
}));

export default useToastStore;
